import { useEffect, useMemo, useState } from 'react';
import { CalendarPlus, RefreshCw, Save, UserPlus, X } from 'lucide-react';
import { createPatientPortalToken, getActiveDivisions, linkMgmReferral, loadMarketingReferralForClient, lookupSatusehatPatientForClient, registerClinicVisit, saveRegistrationClient, searchRegistrationClients, setMgmMarketingReferral } from './data.js';
import { friendlyError, jakartaDateKey } from './lib/format.js';
import { MgmReferralInput } from './MgmReferralInput.jsx';

function addDays(dateKey, days) {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function Field({ label, required = false, wide = false, children, hint }) {
  return (
    <label className={`form-field ${wide ? 'form-field-wide' : ''}`}>
      <span>{label}{required ? ' *' : ''}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function Modal({ title, subtitle, icon, onClose, children }) {
  const Icon = icon;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card" role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-head">
          <div className="modal-title-icon"><Icon size={23} /></div>
          <div className="grow"><h2>{title}</h2><p>{subtitle}</p></div>
          <button className="icon-button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>
        {children}
      </section>
    </div>
  );
}

function satusehatStatusLabel(client) {
  if (!client?.satusehat_consent) return 'Tidak consent';
  const labels = {
    FOUND: 'IHS ditemukan',
    MULTIPLE_FOUND: 'Perlu pilih IHS',
    NOT_FOUND: 'Tidak ditemukan',
    NOT_FOUND_SANDBOX: 'Sandbox: tidak ditemukan',
    ERROR: 'Error'
  };
  return labels[client.satusehat_patient_status] || 'Belum dicek';
}

export function PatientFormModal({ client, references, onClose, onSaved }) {
  const [satusehatClient, setSatusehatClient] = useState(client);
  const [form, setForm] = useState({
    id: client?.id || '',
    name: client?.name || '',
    birth_date: client?.birth_date || '',
    gender: client?.gender || '',
    phone: client?.phone || '',
    address: client?.address || '',
    notes: client?.notes || '',
    client_category: client?.client_category || 'Umum',
    employee_code: client?.employee_code || '',
    employment_unit: client?.employment_unit || '',
    corporate_id: client?.corporate_id || '',
    pricing_policy_id: client?.pricing_policy_id || '',
    nik: client?.nik || '',
    referred_by_code: client?.referred_by_code || '',
    marketing_referrer_id: '',
    satusehat_consent: client?.satusehat_consent || false
  });
  const [busy, setBusy] = useState(false);
  const [satusehatBusy, setSatusehatBusy] = useState(false);
  const [satusehatNotice, setSatusehatNotice] = useState('');
  const [error, setError] = useState('');
  const isInternal = form.client_category === 'Karyawan Internal';
  const today = jakartaDateKey();

  useEffect(() => {
    let cancelled = false;
    if (!client?.id) return undefined;
    loadMarketingReferralForClient(client.id)
      .then((link) => {
        if (!cancelled) {
          if (link?.marketer_user_id) update('marketing_referrer_id', link.marketer_user_id);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [client?.id]);

  function update(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function changeCorporate(value) {
    const corporate = references.corporates.find((row) => row.id === value);
    setForm((current) => ({
      ...current,
      corporate_id: value,
      pricing_policy_id: corporate?.default_pricing_policy_id || current.pricing_policy_id
    }));
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Nama pasien wajib diisi.');
    if (form.birth_date && (form.birth_date < '1900-01-01' || form.birth_date > today)) {
      return setError('Tanggal lahir harus antara 01-01-1900 dan hari ini.');
    }
    if (isInternal && (!form.employee_code.trim() || !form.corporate_id || !form.pricing_policy_id)) {
      return setError('Kode karyawan, corporate, dan kebijakan harga wajib untuk pasien internal.');
    }
    if (form.satusehat_consent && !form.nik.trim()) {
      return setError('NIK wajib diisi jika data dilaporkan ke SatuSehat.');
    }
    setBusy(true);
    try {
      const result = await saveRegistrationClient(form);
      if (!client && (form.referred_by_code?.trim() || form.marketing_referrer_id?.trim())) {
        const savedClient = Array.isArray(result) ? result[0] : result;
        const referredClientId = savedClient?.id || savedClient?.client_id || savedClient?.client?.id;
        if (!referredClientId) throw new Error('Pasien tersimpan, tetapi ID pasien untuk referral tidak tersedia.');
        if (form.referred_by_code?.trim()) await linkMgmReferral(form.referred_by_code, referredClientId);
        if (form.marketing_referrer_id?.trim()) await setMgmMarketingReferral(form.marketing_referrer_id, referredClientId);
      } else if (client?.id) {
        await setMgmMarketingReferral(form.marketing_referrer_id, client.id);
      }
      onSaved(result, client ? 'Data pasien berhasil diperbarui.' : 'Pasien baru berhasil disimpan.');
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  }

  async function checkSatusehat() {
    setError('');
    setSatusehatNotice('');
    const payload = {
      ...satusehatClient,
      id: client?.id,
      nik: form.nik,
      satusehat_consent: form.satusehat_consent
    };
    if (!payload.id) return setError('Simpan pasien dulu sebelum cek SatuSehat.');
    if (!payload.satusehat_consent) return setError('Centang consent SatuSehat sebelum melakukan pengecekan.');
    if (!/^\d{16}$/.test(String(payload.nik || ''))) return setError('NIK harus 16 digit angka.');

    setSatusehatBusy(true);
    try {
      const updated = await lookupSatusehatPatientForClient(payload);
      setSatusehatClient(updated);
      setSatusehatNotice(`Cek SatuSehat selesai: ${satusehatStatusLabel(updated)}.`);
    } catch (lookupError) {
      setError(friendlyError(lookupError));
    } finally {
      setSatusehatBusy(false);
    }
  }

  return (
    <Modal
      title={client ? 'Edit Pasien' : 'Tambah Pasien Baru'}
      subtitle={client ? `Kode ${client.client_code} tidak akan berubah.` : 'Simpan master pasien sebelum membuat kunjungan.'}
      icon={UserPlus}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="modal-body form-grid">
          {error ? <div className="alert alert-danger form-field-wide">{error}</div> : null}
          <Field label="Nama pasien" required>
            <input value={form.name} onChange={(event) => update('name', event.target.value)} maxLength={160} autoFocus required />
          </Field>
          <Field label="Tanggal lahir" hint="Minimum 01-01-1900; tidak boleh melebihi hari ini.">
            <input type="date" min="1900-01-01" max={today} value={form.birth_date} onChange={(event) => update('birth_date', event.target.value)} />
          </Field>
          <Field label="Jenis kelamin">
            <select value={form.gender} onChange={(event) => update('gender', event.target.value)}>
              <option value="">— Pilih —</option>
              <option value="Laki-laki">Laki-laki</option>
              <option value="Perempuan">Perempuan</option>
            </select>
          </Field>
          <Field label="Nomor telepon">
            <input type="tel" value={form.phone} onChange={(event) => update('phone', event.target.value)} maxLength={40} placeholder="Contoh: 0812…" />
          </Field>
          <Field label="Alamat" wide>
            <textarea value={form.address} onChange={(event) => update('address', event.target.value)} rows="2" maxLength={500} />
          </Field>
          <Field label="Catatan" wide>
            <textarea value={form.notes} onChange={(event) => update('notes', event.target.value)} rows="2" maxLength={1000} />
          </Field>
          <Field label="Kategori pasien">
            <select value={form.client_category} onChange={(event) => update('client_category', event.target.value)}>
              <option value="Umum">Umum</option>
              <option value="Karyawan Internal">Karyawan Internal</option>
            </select>
          </Field>
          <MgmReferralInput
            value={form.referred_by_code}
            onChange={(value) => update('referred_by_code', value)}
            label="Kode referral member"
            hint="Opsional — pasien pemberi referral memperoleh komisi 2% setelah transaksi lunas atau paket selesai."
          />
          <Field label="Marketing / Frontliner" hint="Opsional — staf yang melayani referral memperoleh komisi 0,5% pada THP non-Nakes.">
            {references.marketingUsers?.length ? (
              <select value={form.marketing_referrer_id} onChange={(event) => update('marketing_referrer_id', event.target.value)}>
                <option value="">— Tidak ada marketing —</option>
                {references.marketingUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name || user.username} · {user.username || user.id}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={form.marketing_referrer_id}
                onChange={(event) => update('marketing_referrer_id', event.target.value)}
                placeholder="Contoh: username atau ID staf"
                maxLength={80}
                autoComplete="off"
              />
            )}
          </Field>
          <div className="form-field form-field-wide">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={form.satusehat_consent}
                onChange={(event) => update('satusehat_consent', event.target.checked)}
              />
              <span>Setuju data dilaporkan ke <strong>SatuSehat</strong></span>
            </label>
            <small>Jika dicentang, NIK wajib diisi. Data akan masuk antrean pelaporan SatuSehat.</small>
          </div>
          {form.satusehat_consent ? (
            <Field label="NIK (Nomor Induk Kependudukan)" required hint="16 digit, sesuai KTP.">
              <input
                value={form.nik}
                onChange={(event) => update('nik', event.target.value)}
                maxLength={16}
                inputMode="numeric"
                placeholder="3271XXXXXXXXXXXX"
              />
            </Field>
          ) : null}
          {client ? (
            <div className="alert alert-info form-field-wide">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: 1 }}>
                  <strong>Status SatuSehat:</strong> {satusehatStatusLabel(satusehatClient)}
                  {satusehatClient?.satusehat_patient_id ? ` · IHS ${satusehatClient.satusehat_patient_id}` : ''}
                </span>
                <button
                  type="button"
                  className="button button-mini button-soft"
                  disabled={satusehatBusy || busy}
                  onClick={checkSatusehat}
                >
                  <RefreshCw size={14} /> {satusehatBusy ? 'Mengecek...' : 'Cek SATUSEHAT'}
                </button>
              </div>
              {satusehatNotice ? <small>{satusehatNotice}</small> : null}
            </div>
          ) : null}
          {isInternal ? (
            <>
              <Field label="Kode karyawan" required>
                <input value={form.employee_code} onChange={(event) => update('employee_code', event.target.value)} maxLength={100} />
              </Field>
              <Field label="Unit / departemen">
                <input value={form.employment_unit} onChange={(event) => update('employment_unit', event.target.value)} maxLength={160} />
              </Field>
              <Field label="Corporate" required>
                <select value={form.corporate_id} onChange={(event) => changeCorporate(event.target.value)} required>
                  <option value="">— Pilih corporate —</option>
                  {references.corporates.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </select>
              </Field>
              <Field label="Kebijakan harga" required>
                <select value={form.pricing_policy_id} onChange={(event) => update('pricing_policy_id', event.target.value)} required>
                  <option value="">— Pilih kebijakan —</option>
                  {references.pricingPolicies.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </select>
              </Field>
            </>
          ) : null}
        </div>
        <footer className="modal-actions">
          <button type="button" className="button button-quiet" onClick={onClose}>Batal</button>
          <button className="button button-primary" disabled={busy}><Save size={18} />{busy ? 'Menyimpan…' : 'Simpan Pasien'}</button>
        </footer>
      </form>
    </Modal>
  );
}

// Deskripsi per divisi untuk label di form
const DIVISION_DESC = {
  WELLNESS:    { label: 'Wellness / TCM',  desc: 'Akupunktur, Tuina, Bekam, herbal, TCM' },
  ESTETIK:     { label: 'Estetik',         desc: 'Perawatan kulit & kecantikan' },
  DENTAL:      { label: 'Dental',          desc: 'Dokter gigi, scaling, tambal, cabut, ortodonti' },
  DOKTER_UMUM: { label: 'Dokter Umum',     desc: 'Konsultasi umum, MCU, vaksinasi' },
  FISIOTERAPI: { label: 'Fisioterapi',     desc: 'Rehabilitasi, terapi fisik, latihan fungsional' },
  APOTIK:      { label: 'Apotik',          desc: 'Penjualan obat, resep, farmasi' },
};

export function VisitFormModal({ references, activeModules, preferredClient, onClose, onSaved }) {
  const today = jakartaDateKey();
  const divisionOptions = getActiveDivisions(activeModules || { wellness: true, estetik: true })
    .map((d) => ({ value: d.value, ...DIVISION_DESC[d.value] }))
    .filter((d) => d.label); // amankan jika ada key tak dikenal
  const initialDivision = divisionOptions[0]?.value || 'WELLNESS';
  const initialDoctorName = references.staff.find((staff) =>
    !staff.division || staff.division === 'ALL' || staff.division === initialDivision
  )?.name || '';
  const [form, setForm] = useState({
    client_id: preferredClient?.id || '',
    visit_date: today,
    doctor_name: initialDoctorName,
    room_name: '',
    division: initialDivision
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [patientQuery, setPatientQuery] = useState(preferredClient?.name || preferredClient?.client_code || '');
  const [patientResults, setPatientResults] = useState(preferredClient ? [preferredClient] : []);
  const [patientLoading, setPatientLoading] = useState(false);
  const selectedClient = useMemo(
    () => patientResults.find((row) => row.id === form.client_id),
    [patientResults, form.client_id]
  );

  useEffect(() => {
    const query = patientQuery.trim();
    if (query.length < 2 || form.client_id) return undefined;
    const timer = window.setTimeout(async () => {
      setPatientLoading(true);
      try {
        setPatientResults(await searchRegistrationClients(query));
      } catch (lookupError) {
        setError(friendlyError(lookupError));
      } finally {
        setPatientLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [patientQuery, form.client_id]);

  function choosePatient(client) {
    setPatientQuery(`${client.name} · ${client.client_code}`);
    setPatientResults((current) => current.some((row) => row.id === client.id) ? current : [client, ...current]);
    setForm((current) => ({ ...current, client_id: client.id }));
  }

  function clearPatient() {
    setPatientQuery('');
    setPatientResults([]);
    setForm((current) => ({ ...current, client_id: '' }));
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (!form.client_id) return setError('Pilih pasien terlebih dahulu.');
    if (form.visit_date < '2020-01-01' || form.visit_date > addDays(today, 30)) {
      return setError('Tanggal kunjungan maksimal 30 hari dari hari ini.');
    }
    if (!form.doctor_name.trim()) return setError('Nakes atau dokter tujuan wajib dipilih.');
    setBusy(true);
    try {
      const result = await registerClinicVisit(form);
      let portalMessage = '';
      try {
        const access = await createPatientPortalToken(form.client_id, 720);
        portalMessage = ` Token portal pasien: ${access.token}.`;
      } catch {
        portalMessage = ' Token portal pasien belum dibuat otomatis.';
      }
      onSaved(result, `Kunjungan ${result.visit_number} dan invoice awal berhasil dibuat.${portalMessage}`);
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Buat Kunjungan" subtitle="Satu pasien hanya boleh mempunyai satu kunjungan aktif per tanggal." icon={CalendarPlus} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="modal-body form-grid">
          {error ? <div className="alert alert-danger form-field-wide">{error}</div> : null}
          <Field label="Divisi" required wide>
            <div className="division-selector">
              {divisionOptions.map((opt) => (
                <label key={opt.value} className={`division-option ${form.division === opt.value ? 'division-option-active' : ''}`}>
                  <input
                    type="radio"
                    name="division"
                    value={opt.value}
                    checked={form.division === opt.value}
                    onChange={(event) => setForm((current) => ({ ...current, division: event.target.value, doctor_name: '' }))}
                  />
                  <div>
                    <strong>{opt.label}</strong>
                    <span>{opt.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </Field>
          <Field label="Pasien" required wide>
            <div className="patient-search-field">
              <input
                value={patientQuery}
                onChange={(event) => {
                  const value = event.target.value;
                  setPatientQuery(value);
                  if (form.client_id) {
                    setPatientResults([]);
                    setForm((current) => ({ ...current, client_id: '' }));
                  }
                }}
                placeholder="Ketik minimal 2 huruf nama, telepon, atau kode pasien"
                autoFocus
                required={!form.client_id}
              />
              {form.client_id ? <button type="button" className="button button-mini button-quiet" onClick={clearPatient}>Ganti</button> : null}
              {!form.client_id && patientQuery.trim().length >= 2 ? (
                <div className="patient-search-results">
                  {patientLoading ? <div className="patient-search-empty">Mencari pasien...</div> : null}
                  {!patientLoading && !patientResults.length ? <div className="patient-search-empty">Pasien tidak ditemukan.</div> : null}
                  {!patientLoading ? patientResults.map((row) => (
                    <button type="button" key={row.id} onClick={() => choosePatient(row)}>
                      <strong>{row.name}</strong><span>{row.client_code}{row.phone ? ` · ${row.phone}` : ''}</span>
                    </button>
                  )) : null}
                </div>
              ) : null}
            </div>
          </Field>
          {selectedClient ? (
            <div className="selected-client form-field-wide">
              <strong>{selectedClient.name}</strong>
              <span>{selectedClient.client_code} · Lahir {selectedClient.birth_date || '—'} · {selectedClient.phone || 'Tanpa telepon'}</span>
            </div>
          ) : null}
          <Field label="Tanggal kunjungan" required hint="Maksimal 30 hari dari hari ini.">
            <input type="date" min="2020-01-01" max={addDays(today, 30)} value={form.visit_date} onChange={(event) => setForm((current) => ({ ...current, visit_date: event.target.value }))} required />
          </Field>
          <Field label="Nakes / dokter tujuan" required>
            <select value={form.doctor_name} onChange={(event) => setForm((current) => ({ ...current, doctor_name: event.target.value }))} required>
              <option value="">— Pilih nakes —</option>
              {references.staff
                .filter(s => !s.division || s.division === "ALL" || s.division === form.division)
                .map((row) => <option key={row.id} value={row.name}>{row.name}</option>)}
            </select>
          </Field>
          <Field label="Ruangan">
            <input value={form.room_name} onChange={(event) => setForm((current) => ({ ...current, room_name: event.target.value }))} maxLength={100} placeholder="Opsional" />
          </Field>
        </div>
        <footer className="modal-actions">
          <button type="button" className="button button-quiet" onClick={onClose}>Batal</button>
          <button className="button button-primary" disabled={busy}><CalendarPlus size={18} />{busy ? 'Mendaftarkan…' : 'Buat Kunjungan'}</button>
        </footer>
      </form>
    </Modal>
  );
}
